import { useQueryClient } from '@tanstack/react-query';
import { Input } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useEffect, useState } from 'react';
import { updateHealthmetricType } from '../../fetchHooks/updateHealthmetricType';

interface HealthMetricInputDebouncedProps {
  label: string;
  propertyName: string;
  healthmetricId: number;
  value: string;
  style?: React.CSSProperties;
  isNumber?: boolean;
  textArea?: boolean;
}
export const HealthMetricInputDebounced = ({
  label,
  propertyName,
  healthmetricId,
  value,
  style,
  isNumber = false,
  textArea = false,
}: HealthMetricInputDebouncedProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    // Clear the timer when the component unmounts or when inputValue changes
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  if (!propertyName || !healthmetricId) return <p>Err: {label}</p>;

  const isValueUpdated = inputValue.valueOf() === value.valueOf();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event?.target?.value;
    isNumber ? !Number.isNaN(Number(value)) && setInputValue(value) : setInputValue(value);

    // Clear previous timer if any
    if (debounceTimer) clearTimeout(debounceTimer);

    // Set a new timer to send the PUT request after 2 seconds
    const newDebounceTimer = setTimeout(() => {
      updateHealthmetricType({ [propertyName]: value, id: healthmetricId }, () => {
        void queryClient.invalidateQueries({ queryKey: ['useHealthmetrictypes'] });
      });
    }, 2000);

    setDebounceTimer(newDebounceTimer as NodeJS.Timeout);
  };

  if (textArea)
    return (
      <div>
        <p>{label + ':'}</p>
        <TextArea
          placeholder={label}
          value={inputValue}
          status={!isValueUpdated ? 'warning' : undefined}
          autoSize={{ minRows: 20, maxRows: 600 }}
          onChange={handleInputChange}
          style={{ ...style, width: '100%' }}
        />
      </div>
    );
  return (
    <div>
      <Input
        placeholder={label}
        value={inputValue}
        addonBefore={label + ':'}
        status={!isValueUpdated ? 'warning' : undefined}
        onChange={handleInputChange}
        style={style}
      />
    </div>
  );
};
export default HealthMetricInputDebounced;
